import { OfferCalculationsType } from '@/graphql/generated'

export const alertType = {
  bounds: 'Bounds',
  'missing-data': 'Missing Data',
  'suspicious-data': 'Suspicious Data',
  'bad-data': 'Bad Data',
}

export const path = {
  dealSummary: 'summary/',
  credit: 'summary/?component=credit',
  creditFiles: 'summary/?component=credit-files',
  offer: 'summary/?component=offer',
  assets: 'summary/?component=assets',
  commentary: 'summary/?component=commentary',
  'appraisal-report': 'underwriting/?component=appraisal-report',
  'homeowners-insurance': 'underwriting/?component=hoi',
  'closing-details': 'underwriting/?component=closing-details',
  'title-report': 'underwriting/?component=title-report',
  'personal-info': '?component=personal-info',
  property: '?component=property',
  financing: '?component=financing',
  investment: '?component=investment',
  'property-financing': '?component=property-financing',
  propertyDetails: 'valuations/',
  valuations: 'valuations/?component=valuations',
  hoi: 'underwriting/?component=hoi',
  'hoa-dues': 'underwriting/?component=hoa-dues',
  maxViewerCreditReports: 'maxViewerCreditReports',
  dscr: 'structuring/?component=dscr',
  documents: 'documents/',
}

type alertMessageType = {
  /** Human readable message to be displayed */
  message: string
  /** Route to redirect to resolve the alert. It may include the component for auto-scroll */
  path?: string | string[]
  /** SidePanel tab to switch to */
  tab?: string
  /** Unique key for the alert */
  key?: string
}

export const alertMessage: { [key: string]: alertMessageType } = {
  'missing-data-deal-credit-score': {
    message: 'Deal Credit Score',
    path: path['credit'],
  },
  'missing-data-individual-credit-score': {
    message: 'Individual Credit Score',
    path: path['credit'],
  },
  'missing-data-lower-than-550-fico-and-rental-property': {
    message: '< 550 Fico & Rental Property',
    path: path['credit'],
  },
  'bounds-lower-than-550-fico-and-exchange-rate-lower-than-guideline': {
    message: '< 550 Fico & Exchange Rate < {{er_guideline}}',
    path: path['credit'],
  },
  'bounds-fico-is-between-500-to-619-and-the-thfl-is-greater-than-80-percent': {
    message: 'Fico is between 500 to 619 and THFL is greater then 80%',
    path: path['assets'],
  },
  'bounds-fico-is-between-620-to-699-and-the-thfl-is-greater-than-85-percent': {
    message: 'Fico is between 620 to 699 and the THFL is greater than 85%',
    path: path['credit'],
  },
  'bounds-fico-is-700-and-the-thfl-is-greater-than-90-percent': {
    message: 'Fico is 700+ and the THFL is greater than 90%',
    path: path['credit'],
  },
  'missing-data-score-less-than-550-and-dti-is-null': {
    message: 'Credit Score < 550 & DTI is null',
    path: path['credit'],
  },
  'bounds-investment-amount-exceeds-max-investment-allowed': {
    message: 'Investment amount exceeds max investment allowed',
    path: path['offer'],
  },
  'bounds-investment-payment-greater-than-maximum-proceeds-': {
    message: 'Investment Payment > Maximum Proceeds',
    path: path['offer'],
  },
  'missing-data-maximum-proceeds': {
    message: 'Maximum Proceeds',
    path: path['offer'],
  },
  'bounds-thf-origination-greater-than-thfl': {
    message: 'THF @ Origination > THFL',
    path: path['offer'],
  },
  'bounds-negative-net-cash-proceeds': {
    message: 'Negative Net Cash Proceeds',
    path: path['offer'],
  },
  'bounds-unlock-investment-amount-lower-than-30k': {
    message: 'Unlock Investment Amount < 30k',
    path: path['offer'],
  },
  'bounds-term-different-10-years': {
    message: 'Term != 10 Years',
    path: path['assets'],
  },
  'missing-data-dscr-on-rental-property': {
    message: 'DSCR on Rental Property',
    path: path['dscr'],
  },
  'missing-data-property-condition': {
    message: 'Property Condition',
    path: path['appraisal-report'],
  },
  'missing-data-property-sqft-beds-baths': {
    message: 'Property SQFT/Beds/Baths',
    path: path['appraisal-report'],
  },
  'bounds-avm-over-appraisal-variance-greater-than-10-percent': {
    message: 'SV / Appraisal Variance > 10%',
    path: path['appraisal-report'],
  },
  'missing-data-origination-fee-is-null': {
    message: 'Origination Fee is null',
    path: path['closing-details'],
  },
  'suspicious-data-origination-fee': {
    message: 'Origination Fee is not ',
    path: path['closing-details'],
  },
  'missing-data-closing-agent': {
    message: 'Closing Agent',
    path: path['title-report'],
  },
  'missing-data-legal-description': {
    message: 'Legal Description',
    path: path['title-report'],
  },
  'missing-data-proceeds-use': {
    message: 'Proceeds Use',
    path: path['investment'],
  },
  'missing-data-ssn': {
    message: 'Applicant Missing SSN #',
    path: path['personal-info'],
  },
  'missing-data-non-applicant-ssn': {
    message: 'Non Applicant Missing SSN #',
    tab: 'fileContacts',
  },
  'suspicious-data-married-1-applicant-no-consent-of-spouse': {
    message: 'Married, 1 Applicant, No Consent of Spouse',
    path: path['personal-info'],
  },
  'missing-data-duplicate-ssn': {
    message: 'Duplicate SSN #s',
    path: path['personal-info'],
  },
  'missing-data-suffix-in-last-name': {
    message: 'Suffix in Last Name',
    path: path['personal-info'],
  },
  'bad-data-homeowner-born-before-the-1900s': {
    message: 'Homeowner born before the 1900s',
    path: path['personal-info'],
  },
  'suspicious-data-spaces-in-first/last-name': {
    message: 'Spaces In First/Last Name',
    path: path['personal-info'],
  },
  'missing-data-valid-email-address': {
    message: 'Applicant Missing a Valid Email Address',
    path: path['personal-info'],
  },
  'missing-data-non-applicant-valid-email-address': {
    message: 'Non-Applicant Missing a Valid Email Address',
    tab: 'fileContacts',
  },
  'missing-data-non-applicant-missing-citizenship-declaration': {
    message: 'Citizenship declaration for non-applicant is incomplete',
    tab: 'fileContacts',
  },
  'bad-data-last-name-contains-a-comma': {
    message: 'Applicant last name contains a comma',
    path: path['personal-info'],
  },
  'missing-data-home-purchase': {
    message: 'Home Purchase $',
    path: path['property'],
  },
  'missing-data-home-purchase-date': {
    message: 'Home Purchase Date',
    path: path['property'],
  },
  'missing-data-property-type': {
    message: 'Property Type',
    path: path['property'],
  },
  'missing-data-title-held-in-is-blank': {
    message: 'Title Held In is Blank',
    path: path['property'],
  },
  'bad-data-zipcode-length-different-than-5': {
    message: 'Zipcode Length != 5',
    path: path['property'],
  },
  'suspicious-data-vesting-shows-individual-but-we-have-two-applicants': {
    message: 'Vesting shows individual but we have two applicants',
    path: path['property'],
  },
  'suspicious-data-vesting-shows-multiple-owners-and-there-is-one-applicant': {
    message: 'Vesting shows multiple owners and there is {{applicant_count|one}} applicant',
    path: path['property'],
  },
  'bounds-mortgage-rate-greater-than-25-percent': {
    message: 'Mortgage Rate > 25%',
    path: path['financing'],
  },
  'missing-data-mortgage-account-open-date': {
    message: 'Mortgage Account Open Date',
    path: path['financing'],
  },
  'missing-data-secured-debt': {
    message: 'Secured Debt',
    path: path['financing'],
  },
  'bounds-secured-debt-greater-or-equals-primary-home-value': {
    message: 'Secured Debt >= Primary Home Value',
    path: path['financing'],
  },
  'bounds-secured-debt-greater-or-equals-avm-value': {
    message: 'Secured Debt >= AVM Value',
    path: path['financing'],
  },
  'missing-data-missing-mortgage-rate': {
    message: 'Missing Mortgage Rate',
    path: path['financing'],
  },
  'missing-data-missing-mortgage-type': {
    message: 'Missing Mortgage Type',
    path: path['financing'],
  },
  'bounds-unlock-in-3rd-lien': {
    message: 'Unlock in 3rd+ Lien',
    path: path['financing'],
  },
  'bounds-2-unapproved-exceptions-raised': {
    message: '2 Unapproved Exceptions Raised',
    path: path['commentary'],
  },
  'hometap-inquiries': {
    message: 'HOMETAP Inquiries',
    path: path['creditFiles'],
  },
  'no-mortgage-debt-on-the-property-has-not-been-confirmed': {
    message: 'No mortgage debt on the property has not been confirmed',
    path: path['financing'],
  },
  'suspicious-data-application-exists-for-ssn': {
    message: 'An application SSN exists in other Unlock deals',
    path: path['dealSummary'],
  },
  'suspicious-data-application-exists-for-property-address': {
    message: 'An application Subject Property Address exists in other Unlock deals',
    path: path['dealSummary'],
  },
  'missing-data-mortgage-monthly-payment': {
    message: 'A lien holder is missing Monthly Payment',
    path: path['financing'],
  },
  'missing-data-interest-rate': {
    message: 'A lien holder is missing Interest Rate',
    path: path['financing'],
  },
  'missing-data-amortization-type': {
    message: 'A lien holder is missing the Amortization Type',
    path: path['financing'],
  },
  'missing-data-mortgage-account-number': {
    message: 'A lien holder is missing the Account Number',
    path: path['financing'],
  },
  'missing-data-homeowners-insurance-info': {
    message: 'Missing HOI Information',
    path: path['homeowners-insurance'],
  },
  'missing-data-first-lien-escrow-status': {
    message: 'Is first lien holder Escrowed?',
    path: path['financing'],
  },
  'missing-data-first-lien-escrow-amount': {
    message: 'The first lien holder is missing the Escrow Amount',
    path: path['financing'],
  },
  'missing-data-property-address': {
    message: 'Missing Property Address information',
    path: path['property'],
  },
  'missing-data-mailing-address': {
    message: 'Missing Mailing Address information',
    path: path['personal-info'],
  },
  'missing-data-co-owner-address': {
    message: 'Missing Co Owner Address information',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-street': {
    message: 'Missing Co Owner Street',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-zipcode': {
    message: 'Missing Co Owner Zipcode',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-state': {
    message: 'Missing Co Owner State',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-city': {
    message: 'Missing Co Owner City',
    tab: 'fileContacts',
  },
  'missing-data-parcel-number': {
    message: 'Missing Parcel Number',
    path: path['property'],
  },
  'missing-data-co-owner-missing-citizenship-declaration': {
    message: 'Citizenship declaration for {{full_name|Co-Owner}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-missing-full-name': {
    message: 'Full Name for {{full_name|Co-Owner}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-married-to-missing': {
    message: 'Married To field for {{full_name|Co-Owner}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-dob-missing': {
    message: 'DOB for {{full_name|Co-Owner}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-ssn-missing': {
    message: 'SSN for {{full_name|Co-Owner}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-phone-missing': {
    message: 'Phone for {{full_name|Co-Owner}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-email-missing': {
    message: 'Email for {{full_name|Co-Owner}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-current-residence-incomplete': {
    message: 'Current Residence for {{full_name|Co-Owner}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-photo-id-expiration-missing': {
    message: 'Photo ID expire date for {{full_name|Co-Owner}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-co-owner-id-missing': {
    message: '{{full_name|Co-Owner}} ID is missing',
    path: path['documents'],
  },
  'missing-data-noo-full-name-missing': {
    message: 'Full Name for {{full_name|NOO}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-noo-married-to-missing': {
    message: 'Married To field for {{full_name|NOO}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-noo-dob-missing': {
    message: 'DOB for {{full_name|NOO}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-noo-ssn-missing': {
    message: 'SSN for {{full_name|NOO}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-noo-phone-missing': {
    message: 'Phone for {{full_name|NOO}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-noo-email-missing': {
    message: 'Email for {{full_name|NOO}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-noo-photo-id-expiration-missing': {
    message: 'Photo ID expire date for {{full_name|NOO}} is incomplete',
    tab: 'fileContacts',
  },
  'missing-data-noo-id-missing': {
    message: '{{full_name|NOO}} ID is missing',
    path: path['documents'],
  },
  'eligible-state-missing': {
    message: 'Property is not in an Eligible State',
    path: path['property'],
  },
  'missing-data-frozen-credit': {
    message: 'Frozen Credit',
    path: path['creditFiles'],
  },
  'suspicious-data-flood-zone-mismatch': {
    message: 'Flood Zone mismatch',
    path: path['appraisal-report'],
  },
  'cost-benefit-ratio-below-5': {
    message: 'Cost-Benefit-Ratio below 5',
    path: path['offer'],
  },
  'suspicious-data-abbreviation_nickname-for-first-name': {
    message: 'Abbreviation/Nickname for First Name',
    path: path['personal-info'],
  },
  'suspicious-data-credit-report-name-or-ssn-does-not-match-application': {
    message: 'Credit Report name or SSN does not match application',
    path: [path['personal-info'], path['maxViewerCreditReports']],
  },
  'suspicious-name-does-not-match-title-report': {
    message: 'Applicant Name does not match Title Report',
    path: path['personal-info'],
  },
  'suspicious-co-owner-names-dont-match-title-report': {
    message: 'One or more co-owners / co-applicants do not match the Title Report',
    tab: 'fileContacts',
  },
  'suspicious-marital-status-does-not-match-title-report': {
    message: 'Marital Status does not match Title Report',
    path: path['personal-info'],
  },
  'bounds-flood-zone-code': {
    message: 'Property is in Flood Zone',
    path: path['hoi'],
  },
  'suspicious-data-property-flagged-for-atypicality': {
    message: 'Property Flagged for Atypicality',
    path: path['propertyDetails'],
  },
  'suspicious-data-roof-solar-panel-may-exist-on-property': {
    message: 'Roof Solar Panel May Exist on Property',
    path: path['propertyDetails'],
  },
  'suspicious-data-rental-property-mailing-and-subject-property-address-match': {
    message: 'Rental Property: Mailing Address = Subject Property Address',
    path: path['property'],
  },
  'suspicious-data-second-home-mailing-and-subject-property-address-match': {
    message: 'Second Home: Mailing Address = Subject Property Address',
    path: path['property'],
  },
  'suspicious-data-active-application-exists-for-ssn': {
    message:
      'The applicant has other active Unlock applications running concurrently or has an HEA with Unlock.',
    path: path['dealSummary'],
  },
  'aggregate-investment-exceeds-max': {
    message: 'Unlock Aggregate Investment Payment exceeds $500k',
    path: path['offer'],
  },
  'bad-data-credit-report-does-not-match-summary': {
    message: 'Credit report applicant Mid-Score does not matches Summary view',
    path: path['credit'],
  },
  'bad-data-avm-not-available': {
    message: 'AVM Not Available',
    path: path['valuations'],
  },
  'bounds-co-owner-is-not-us-citizen': {
    message: 'Co-Owner is not a US Citizen or a Permanent Resident.',
    tab: 'fileContacts',
  },
  'hoa-balance-due': {
    message: 'HOA Dues are not current.',
    path: path['hoa-dues'],
  },
  'bounds-credit-report-has-bankruptcy-last-5-years': {
    message: 'Credit Report indicates a BK >= 3 years and <= 5 years',
    path: [path['credit'], path['maxViewerCreditReports']],
  },
  'bounds-credit-report-has-bankruptcy-last-3-years': {
    message: 'Credit Report indicates a BK < 3 years',
    path: [path['credit'], path['maxViewerCreditReports']],
  },
  'bounds-credit-report-has-foreclosure-last-5-years': {
    message: 'Credit Report indicates a FC >= 3 years and <= 5 years',
    path: [path['credit'], path['maxViewerCreditReports']],
  },
  'bounds-credit-report-has-foreclosure-last-3-years': {
    message: 'Credit Report indicates a FC < 3 years',
    path: [path['credit'], path['maxViewerCreditReports']],
  },
  'bounds-credit-report-has-short-sale-last-5-years': {
    message: 'Credit Report indicates a SS >= 3 years and <= 5 years',
    path: [path['credit'], path['maxViewerCreditReports']],
  },
  'bounds-credit-report-has-short-sale-last-3-years': {
    message: 'Credit Report indicates a SS < 3 years',
    path: [path['credit'], path['maxViewerCreditReports']],
  },
  'bounds-credit-report-has-deed-in-lieu-last-5-years': {
    message: 'Credit Report indicates a DIL >= 3 years and <= 5 years',
    path: [path['credit'], path['maxViewerCreditReports']],
  },
  'bounds-credit-report-has-deed-in-lieu-last-3-years': {
    message: 'Credit Report indicates a DIL < 3 years',
    path: [path['credit'], path['maxViewerCreditReports']],
  },
  'bounds-credit-report-has-90-days-mortgage-delinquency-last-2-years': {
    message: 'Credit Report indicates 90 day MTG DQ within 2 years',
    path: [path['credit'], path['maxViewerCreditReports']],
  },
  'bounds-credit-report-has-120-days-mortgage-delinquency-last-2-years': {
    message: 'Credit Report indicates 120+ day MTG DQ within 2 years',
    path: [path['credit'], path['maxViewerCreditReports']],
  },
  'bounds-avm-appraisal-variance-less-than-10-percent': {
    message: 'SV / Appraisal Variance < 10%',
    path: path['appraisal-report'],
  },
  'bounds-payoff-expired': {
    message: '{{creditor|Creditor}} payoffs Expired',
    path: path['financing'],
  },
  'bounds-home-value-less-than-175k': {
    message: 'Home Value < $175k',
    path: path['valuations'],
  },
  'bounds-investment-size-greater-than-500k': {
    message: 'Investment Size > $500k',
    path: path['offer'],
  },
  'bounds-credit-report-expires-in-n-days': {
    message: 'Credit Report expires in {{days_until_expiration|0}} day(s)',
    path: [path['credit'], path['maxViewerCreditReports']],
  },
  'bounds-credit-report-expired': {
    message: 'Credit Report Expired',
    path: path['credit'],
  },
  'bounds-hoi-expired': {
    message: '{{insurance_company|Insurance Company}} HOI Expired',
    path: path['hoi'],
  },
  'bounds-appraisal-report-expired': {
    message: 'Appraisal Report Expired',
    path: path['appraisal-report'],
  },
  'bounds-title-report-expired': {
    message: 'Title Report Expired',
    path: path['title-report'],
  },
  'bounds-appraisal-report-expires-in-n-days': {
    message: 'Appraisal Report expires in {{days_until_expiration|0}} day(s)',
    path: path['appraisal-report'],
  },
  'bounds-payoff-expires-in-n-days': {
    message: '{{creditor|Creditor}} payoff expires in {{days_until_expiration|0}} day(s)',
    path: path['financing'],
  },
  'bounds-hoi-expires-in-n-days': {
    message:
      '{{insurance_company|Insurance Company}} HOI expires in {{days_until_expiration|0}} day(s). HOI renewal needed',
    path: path['hoi'],
  },
  'bounds-hoi-renewal-in-n-days': {
    message:
      '{{insurance_company|Insurance Company}} HOI must be renewed in {{days_until_renewal|0}} day(s)',
    path: path['hoi'],
  },
  'bounds-photo-id-expires-in-n-days-applicants': {
    message: '{{full_name|Applicant}} Photo ID expires in {{days_until_expiration|0}} day(s)',
    path: path['personal-info'],
  },
  'bounds-photo-id-expires-in-n-days-non-applicants': {
    message: '{{full_name|Non-Applicant}} Photo ID expires in {{days_until_expiration|0}} day(s)',
    tab: 'fileContacts',
  },
  'bounds-photo-id-expired': {
    message: '{{full_name|Applicant}} Photo ID Expired',
    path: path['personal-info'],
  },
  'bounds-title-report-expires-in-n-days': {
    message: 'Title Report expires in {{days_until_expiration|0}} day(s)',
    path: path['title-report'],
  },
  'bounds-dscr-less-than-1dot25-with-all-units-leased': {
    message: 'DSCR < 1.25 with all units leased',
    path: path['dscr'],
  },
  'bounds-dscr-greater-than-1dot25-with-at-least-1-unit-not-leased': {
    message: 'DSCR >= 1.25 with at least one unit not leased',
    path: path['dscr'],
  },
  'bounds-property-condition-is-c5': {
    message: 'Property Condition = C5',
    path: path['appraisal-report'],
  },
  'suspicious-data-title-vesting-names-mismatch-applicant': {
    message: 'Title Vesting Names Mismatch for {{name|Applicant}}',
    path: path['personal-info'],
  },
  'suspicious-data-title-vesting-names-mismatch-non-applicant': {
    message: 'Title Vesting Names Mismatch for {{name|Non-Applicant}}',
    tab: 'fileContacts',
  },
  'bounds-hoa-balance-due-in-n-days': {
    message: '{{company}} HOA Balance Due in {{days_until_due|0}} day(s)',
    path: path['hoa-dues'],
  },
  'bounds-hoa-balance-is-past-due': {
    message: '{{company}} HOA Balance is Past Due',
    path: path['hoa-dues'],
  },
  'bounds-investment-amount-is-zero': {
    message: 'This deal has $0 Investment. An offer cannot be made. Reason: {{reason}}',
    path: path['offer'],
  },
  'missing-data-primary-applicant-dob-missing': {
    message: '{{full_name|Applicant}} Missing Date of Birth',
    path: path['personal-info'],
  },
  'missing-data-co-applicant-dob-missing': {
    message: '{{full_name|Co-Applicant}} Missing Date of Birth',
    path: path['personal-info'],
  },
}

export const ficoAlerts = [
  'missing-data-lower-than-550-fico-and-rental-property',
  'bounds-lower-than-550-fico-and-exchange-rate-lower-than-guideline',
  'bounds-fico-is-between-500-to-619-and-the-thfl-is-greater-than-80-percent',
  'bounds-fico-is-between-620-to-699-and-the-thfl-is-greater-than-85-percent',
  'bounds-fico-is-700-and-the-thfl-is-greater-than-90-percent',
]

/**
 * Replaces the `Fico` label with `VScore` if the pricing version is 15 or above.
 * It was decoded by product to use the pricing version is used instead of the `isVantage` flag,
 * as this simplifies the use case and it will reflect the correct info in 99% of deals (there are
 * a couple deals in pricing version 13 and 14 with the `isVantage` flag, but those will be ignored)
 *
 * @param message
 * @param pricing_version
 * @returns string
 */
export const adjustLabelForFicoAlertMessage = (message: string, pricing_version: number) => {
  return pricing_version >= 15 ? message.replace('Fico', 'Vscore') : message
}
